import { useState, useEffect, useCallback } from 'react'
import { Component, LocaleCode } from '../types/component'
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover'

interface WishlistProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
  wishlistId?: string | null
  onWishlistUpdate?: (wishlistId: string) => void
}

interface WishlistItem {
  id: string
  product_id: number
  variant_id?: number
  product: {
    name: string
    sku?: string
    price: number
    image_url?: string
  }
}

interface WishlistData {
  id: string
  name: string
  items: WishlistItem[]
}

export function Wishlist({ component, locale, storeId, apiUrl, style, wishlistId, onWishlistUpdate }: WishlistProps) {
  const [wishlistData, setWishlistData] = useState<WishlistData | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const config = component.config as any

  const displayStyle = (config.displayStyle as string) || 'popover'
  const buttonText = (config.buttonText as string) || ''
  const showItemCount = (config.showItemCount as boolean) !== false

  const loadWishlist = useCallback(async () => {
    if (!wishlistId || !storeId) return

    try {
      setIsLoading(true)
      const response = await fetch(`${apiUrl}/api/v1/bigcommerce/${storeId}/wishlists/${wishlistId}`)
      if (!response.ok) {
        if (response.status === 404) {
          setWishlistData(null)
          if (onWishlistUpdate) {
            onWishlistUpdate('')
          }
        }
        throw new Error('Failed to load wishlist')
      }
      const data = await response.json()
      setWishlistData(data.data)
    } catch (error) {
      console.error('Failed to load wishlist:', error)
      setWishlistData(null)
    } finally {
      setIsLoading(false)
    }
  }, [wishlistId, storeId, apiUrl, onWishlistUpdate])

  useEffect(() => {
    if (wishlistId && storeId) {
      loadWishlist()
    }
  }, [wishlistId, storeId, loadWishlist])

  useEffect(() => {
    if (isOpen && wishlistId && storeId) {
      loadWishlist()
    }
  }, [isOpen, wishlistId, storeId, loadWishlist])

  const getTotalItems = () => {
    return wishlistData?.items?.length || 0
  }

  const handleRemoveItem = async (itemId: string) => {
    if (!wishlistId || !storeId) return

    try {
      const response = await fetch(`${apiUrl}/api/v1/bigcommerce/${storeId}/wishlists/${wishlistId}/items/${itemId}`, {
        method: 'DELETE',
      })
      if (!response.ok) throw new Error('Failed to remove item')
      await loadWishlist()
    } catch (error) {
      console.error('Failed to remove item:', error)
    }
  }

  const renderWishlistButton = () => {
    const isIconStyle = displayStyle === 'icon'

    return (
      <button className={
        isIconStyle
          ? "relative p-2 hover:bg-accent rounded transition-colors"
          : "relative flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90 transition-colors"
      }>
        {/* Heart Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
        </svg>

        {buttonText && !isIconStyle && <span>{buttonText}</span>}

        {/* Item Count Badge */}
        {showItemCount && getTotalItems() > 0 && (
          <span className={
            isIconStyle
              ? "absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium"
              : "absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center"
          }>
            {getTotalItems()}
          </span>
        )}
      </button>
    )
  }

  const renderWishlistContent = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold text-lg">Wishlist</h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-muted-foreground hover:text-foreground transition-colors text-2xl leading-none"
        >
          ×
        </button>
      </div>

      {/* Wishlist Items */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading wishlist...</div>
        ) : !wishlistData || wishlistData.items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>Your wishlist is empty</p>
          </div>
        ) : (
          <div className="space-y-4">
            {wishlistData.items.map((item) => (
              <div key={item.id} className="flex gap-3 pb-4 border-b last:border-b-0">
                {/* Product Image */}
                <div className="w-20 h-20 bg-muted rounded overflow-hidden flex-shrink-0">
                  {item.product.image_url ? (
                    <img src={item.product.image_url} alt={item.product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                      No Image
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-medium text-sm line-clamp-2 flex-1">{item.product.name}</h4>
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors ml-2"
                      title="Remove item"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                  {item.product.sku && <p className="text-xs text-muted-foreground mb-2">SKU: {item.product.sku}</p>}
                  <p className="text-sm font-bold">${item.product.price.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  // Render popover style
  if (displayStyle === 'popover') {
    return (
      <div style={style}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            {renderWishlistButton()}
          </PopoverTrigger>
          <PopoverContent align="end" className="w-96 max-h-[600px] p-0">
            {renderWishlistContent()}
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  // Render sidebar style
  return (
    <div style={style} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90 transition-colors"
      >
        {/* Heart Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
        </svg>

        {buttonText && <span>{buttonText}</span>}

        {/* Item Count Badge */}
        {showItemCount && getTotalItems() > 0 && (
          <span className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {getTotalItems()}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-96 bg-background shadow-2xl z-50 animate-slide-in-right">
            {renderWishlistContent()}
          </div>
        </>
      )}
    </div>
  )
}
